import { formatDate, getDateByTimeZone } from "@/lib/date";

import BaseModel from "./base-model";
import { parseDeliveryStatus, renderStatus, renderStatusByNumber } from "./Delivery";

export default class Cs extends BaseModel {
  renderRegistrationType() {
    switch (this.registrationType) {
      case "CUSTOMER":
        return "고객";
      case "CORP":
        return "화주사";
      case "ADMIN":
        return "회사";
      default:
        return this.registrationType || "";
    }
  }

  renderChecked() {
    if (this.deleted) {
      return "취소";
    }

    return this.checked ? "처리완료" : "처리대기";
  }

  renderDateTime(value) {
    const zoned = getDateByTimeZone(value);

    if (!zoned) {
      return "";
    }

    return formatDate(zoned, "yyyy-MM-dd HH:mm");
  }

  renderCreatedAt() {
    return this.renderDateTime(this.createdAt);
  }

  renderCompletedAt() {
    return this.renderDateTime(this.completedAt);
  }

  renderLatestCommentWriterName() {
    const latest = this.getLatestComment();

    if (latest) {
      return latest.getWriterName();
    }

    return this.latestCommentWriterName || "";
  }

  getLatestComment() {
    if (!this.comments || !this.comments.length) {
      return null;
    }

    return this.comments[this.comments.length - 1];
  }

  applyComments(comments = []) {
    this.comments = comments;

    const latest = this.getLatestComment();

    if (latest) {
      this.latestCommentWriterName = latest.getWriterName();
      this.latestCommentCreatedAt = latest.createdAt;
    }

    return this;
  }

  renderLatestCommentCreatedAt() {
    return this.renderDateTime(this.latestCommentCreatedAt);
  }

  renderDeliveryStatus() {
    const status =
      this.delivery?.status ?? this.deliveryStatus;

    if (status == null || status === "") {
      return "";
    }

    if (typeof status === "number" || /^\d+$/.test(String(status))) {
      return renderStatus(parseDeliveryStatus(status));
    }

    const labeled = renderStatus(status);

    return labeled === "접수" && status !== "DELIVERY_STATUS_RECEIPT"
      ? status
      : labeled;
  }

  renderBookId() {
    return this.bookId || this.delivery?.bookId || "";
  }

  renderOrderIdFromCorp() {
    return this.orderIdFromCorp || this.delivery?.orderIdFromCorp || "";
  }

  renderOrderIdFromCorpSub() {
    return this.orderIdFromCorpSub || this.delivery?.orderIdFromCorpSub || "";
  }

  renderText() {
    return this.text || "";
  }

  renderAnswer() {
    return this.answer || "";
  }

  renderCommentConfirmed() {
    if (this.commentConfirmed == null) {
      return "";
    }

    return this.commentConfirmed ? "확인" : "미확인";
  }

  isCanceled() {
    return Boolean(this.deleted);
  }

  isProcessed() {
    return this.checked === true || this.checked === 1 || this.checked === "1";
  }

  canCorpEdit() {
    return (
      this.registrationType === "CORP" &&
      !this.isCanceled() &&
      !this.isProcessed()
    );
  }

  renderRegistrant() {
    let side = this.renderRegistrationType();

    if (this.registrationType === "ADMIN") {
      side = "관제";
    } else if (this.registrationType === "CORP") {
      side = "파트너웹";
    }

    const name =
      this.writerName ||
      this.createdByName ||
      this.registrantName ||
      this.corpUser?.realname ||
      this.corpUser?.corpTitle ||
      this.adminUser?.realname ||
      "";

    if (side && name) {
      return `${side} > ${name}`;
    }

    return name || side || "";
  }

  renderCheckedMark() {
    if (this.deleted) {
      return "취소";
    }

    return this.checked ? "O" : "X";
  }

  renderProcessDetail() {
    const at = this.renderCompletedAt();
    const method =
      this.processType ||
      this.processMethod ||
      this.completionType ||
      this.completedType ||
      "";
    const rider =
      this.riderName ||
      this.deliveryRiderName ||
      this.processedRiderName ||
      this.deliveryRider?.name ||
      "";

    if (method && rider) {
      return [at, `${method}(라이더: ${rider})`].filter(Boolean).join(" ");
    }

    if (method) {
      return [at, method].filter(Boolean).join(" ");
    }

    if (rider) {
      return [at, `(라이더: ${rider})`].filter(Boolean).join(" ");
    }

    return at;
  }

  get csEtcObj() {
    try {
      return this.etc && JSON.parse(this.etc);
    } catch (_e) {
      return {};
    }
  }

  get updateLogsObj() {
    try {
      return this.updateLogs && JSON.parse(this.updateLogs);
    } catch (_e) {
      return {};
    }
  }

  get updateLogsArray() {
    try {
      return this.updateLogsObj ? Object.entries(this.updateLogsObj) : [];
    } catch (_e) {
      return [];
    }
  }

  get withDeliveryCompleted() {
    return this.csEtcObj && this.csEtcObj.withDeliveryCompleted;
  }

  getProcessUser(log) {
    if (!log) {
      return "";
    }

    if (log.admin) {
      const name = log.admin.realname || log.admin.username || "";
      return name ? `${name}(관리자)` : "";
    }

    if (log.rider) {
      const name = log.rider.name || "";
      return name ? `${name}(라이더)` : "";
    }

    if (log.realname) {
      return `${log.realname}(관리자)`;
    }

    return "";
  }

  getProcessBadge(log) {
    if (this.deleted) {
      return "취소";
    }

    if (!this.checked) {
      return "처리대기";
    }

    if (log?.collected) {
      return "회수완료";
    }

    const statusLabel = renderStatusByNumber(log?.status);
    if (statusLabel) {
      return statusLabel;
    }

    if (this.withDeliveryCompleted) {
      return "배송완료";
    }

    return "단순완료";
  }

  getProcessInfo() {
    const processLogs = (this.updateLogsArray || []).filter(([, log]) => {
      if (!log) {
        return false;
      }

      return (
        log.checked === true ||
        log.collected ||
        (log.status !== null && log.status !== undefined)
      );
    });
    const lastLog = processLogs[processLogs.length - 1];
    const log = lastLog?.[1];

    if (lastLog) {
      return {
        badge: this.getProcessBadge(log),
        at: this.renderDateTime(lastLog[0]),
        user: this.getProcessUser(log),
      };
    }

    if (this.checked && this.completedAt) {
      return {
        badge: this.getProcessBadge(),
        at: this.renderCompletedAt(),
        user: "",
      };
    }

    return {
      badge: this.getProcessBadge(),
      at: "",
      user: "",
    };
  }
}
